/**
 * E.164 Based Phone Number Validation for WebRTC Calling
 * Supports Australia (+61), New Zealand (+64), and Malaysia (+60)
 */
import { COUNTRY_CONFIG } from '../../constants/config';

/**
 * Normalize phone number by removing spaces, dashes, parentheses
 * @param {string} number - Phone number to normalize
 * @returns {string} Normalized phone number
 */
export function normalizePhoneNumber(number) {
    if (!number || typeof number !== 'string') {
        return '';
    }
    return number.replace(/[\s\-\(\)]/g, '');
}

/**
 * Check if a number is in E.164 format
 * @param {string} number - Phone number to check
 * @returns {boolean} True if in E.164 format
 */
export function isE164Format(number) {
    return /^\+[1-9]\d{1,14}$/.test(number);
}

/**
 * Convert national format to E.164 format
 * @param {string} number - Phone number in national format
 * @param {string} country - Country code (au, nz, my)
 * @returns {string} Phone number in E.164 format
 */
export function toE164(number, country) {
    const normalized = normalizePhoneNumber(number);
    const config = COUNTRY_CONFIG[country];

    if (!config) {
        return '';
    }

    // If already in E.164 format, return as is
    if (isE164Format(normalized)) {
        return normalized;
    }

    // If starts with country code (without +), add +
    if (normalized.startsWith(config.dialCodeWithoutPlus)) {
        return `+${normalized}`;
    }

    // If starts with 0 (national format), replace with country code
    if (normalized.startsWith('0')) {
        return `+${config.dialCodeWithoutPlus}${normalized.substring(1)}`;
    }

    // Otherwise, assume it's already in correct format
    return `+${normalized}`;
}

/**
 * Convert E.164 format to national format
 * @param {string} number - Phone number in E.164 format
 * @param {string} country - Country code (au, nz, my)
 * @returns {string} Phone number in national format
 */
export function toNational(number, country) {
    const normalized = normalizePhoneNumber(number);
    const config = COUNTRY_CONFIG[country];

    if (!config) {
        return '';
    }

    // Remove + and country code
    if (normalized.startsWith('+')) {
        return `0${normalized.substring(3)}`;
    }

    return `0${normalized.substring(2)}`;
}

/**
 * Validate phone number for a specific country
 * @param {string} number - Phone number to validate
 * @param {string} country - Country code (au, nz, my)
 * @returns {object} Validation result with isValid and errorMessage
 */
export function validatePhoneNumber(number, country) {
    const normalized = normalizePhoneNumber(number);

    const config = COUNTRY_CONFIG[country];

    if (!config) {
        return {
            isValid: false,
            errorMessage: 'Unsupported country',
        };
    }

    // Empty number is invalid
    if (!normalized) {
        return {
            isValid: false,
            errorMessage: 'Please enter a phone number',
        };
    }

    // Check if in E.164 format
    if (isE164Format(normalized)) {
        // Extract the number after country code
        const numberWithoutCountryCode = normalized.substring(3);

        // Prepend 0 to match the national format patterns
        const nationalNumber = `0${numberWithoutCountryCode}`;

        // Check if it matches any valid pattern
        const matchesPattern = config.validPatterns.some((pattern) =>
            pattern.test(nationalNumber)
        );

        if (matchesPattern) {
            return {
                isValid: true,
                errorMessage: null,
            };
        }

        return {
            isValid: false,
            errorMessage: `Invalid ${config.name} phone number format`,
        };
    }

    // Check if starts with 0 (national format)
    if (normalized.startsWith('0')) {
        const matchesPattern = config.validPatterns.some((pattern) =>
            pattern.test(normalized)
        );

        if (matchesPattern) {
            return {
                isValid: true,
                errorMessage: null,
            };
        }

        return {
            isValid: false,
            errorMessage: `Invalid ${config.name} phone number format`,
        };
    }

    // Check if starts with country code (without +)
    if (normalized.startsWith(config.dialCodeWithoutPlus)) {
        const numberWithoutCountryCode = normalized.substring(2);
        const matchesPattern = config.validPatterns.some((pattern) =>
            pattern.test(`0${numberWithoutCountryCode}`)
        );

        if (matchesPattern) {
            return {
                isValid: true,
                errorMessage: null,
            };
        }

        return {
            isValid: false,
            errorMessage: `Invalid ${config.name} phone number format`,
        };
    }

    return {
        isValid: false,
        errorMessage: `Phone number must start with ${config.dialCode} or 0`,
    };
}

/**
 * Validate phone number against multiple countries
 * @param {string} number - Phone number to validate
 * @param {string[]} countries - Array of country codes to check against
 * @returns {object} Validation result with isValid, matchedCountry, and errorMessage
 */
export function validatePhoneNumberForCountries(number, countries) {
    const normalized = normalizePhoneNumber(number);

    if (!normalized) {
        return {
            isValid: false,
            matchedCountry: null,
            errorMessage: 'Please enter a phone number',
        };
    }

    for (const country of countries) {
        const result = validatePhoneNumber(number, country);
        if (result.isValid) {
            return {
                isValid: true,
                matchedCountry: country,
                errorMessage: null,
            };
        }
    }

    // If no country matched, return error for the first country
    if (countries.length > 0) {
        const firstCountryResult = validatePhoneNumber(number, countries[0]);
        return {
            isValid: false,
            matchedCountry: null,
            errorMessage: firstCountryResult.errorMessage,
        };
    }

    return {
        isValid: false,
        matchedCountry: null,
        errorMessage: 'No valid countries specified',
    };
}

/**
 * Format phone number for display
 * @param {string} number - Phone number to format
 * @param {string} country - Country code (au, nz, my)
 * @returns {string} Formatted phone number
 */
export function formatPhoneNumber(number, country) {
    const normalized = normalizePhoneNumber(number);
    const config = COUNTRY_CONFIG[country];

    if (!config) {
        return normalized;
    }

    // If in E.164 format, convert to national format first
    if (isE164Format(normalized)) {
        const numberWithoutCountryCode = normalized.substring(3);
        return formatNationalNumber(numberWithoutCountryCode, country);
    }

    return formatNationalNumber(normalized, country);
}

/**
 * Format national number for display
 * @param {string} number - National format number
 * @param {string} country - Country code (au, nz, my)
 * @returns {string} Formatted phone number
 */
function formatNationalNumber(number, country) {
    const config = COUNTRY_CONFIG[country];

    if (!config) {
        return number;
    }

    // Australia: 04xx xxx xxx or 02xx xxx xxx
    if (country === 'au') {
        if (
            /^04\d{8}$/.test(number) ||
            /^05\d{8}$/.test(number) ||
            /^07\d{8}$/.test(number) ||
            /^08\d{8}$/.test(number)
        ) {
            return `${number.substring(0, 4)} ${number.substring(4, 7)} ${number.substring(7)}`;
        }
        if (/^02\d{8}$/.test(number) || /^03\d{8}$/.test(number)) {
            return `${number.substring(0, 2)} ${number.substring(2, 6)} ${number.substring(6)}`;
        }
    }

    // New Zealand: 02xx xxx xxxx or 03xx xxx xxx
    if (country === 'nz') {
        // New Zealand mobile numbers (02x) are formatted as 02x xxx xxxx (3-3-4)
        if (/^02\d{7,9}$/.test(number)) {
            return `${number.substring(0, 3)} ${number.substring(3, 6)} ${number.substring(6)}`;
        }
        // New Zealand landline numbers (03xx, 04xx, 06xx, 07xx, 09xx) are formatted as 0xx xxx xxxx (2-3-4)
        if (/^0[34679]\d{7}$/.test(number)) {
            return `${number.substring(0, 2)} ${number.substring(2, 5)} ${number.substring(5)}`;
        }
    }

    // Malaysia: 01xx xxx xxx or 03xx xxx xxx
    if (country === 'my') {
        // Malaysian mobile numbers (01x) are formatted as 01x xxx xxxx (3-3-4)
        if (/^01\d{8,9}$/.test(number)) {
            return `${number.substring(0, 3)} ${number.substring(3, 6)} ${number.substring(6)}`;
        }
        // Malaysian landline numbers (03xx, 04xx, 05xx, 06xx, 08xx, 09xx) are formatted as 0xx xxx xxxx (2-3-4)
        if (/^0[345689]\d{7,8}$/.test(number)) {
            return `${number.substring(0, 2)} ${number.substring(2, 6)} ${number.substring(6)}`;
        }
    }

    return number;
}

/**
 * Get country info from phone number
 * @param {string} number - Phone number
 * @param {string[]} countries - Array of country codes to check against
 * @returns {object} Country info or null if not matched
 */
export function getCountryFromPhoneNumber(
    number,
    countries = Object.keys(COUNTRY_CONFIG)
) {
    for (const country of countries) {
        const result = validatePhoneNumber(number, country);
        if (result.isValid) {
            return COUNTRY_CONFIG[country];
        }
    }
    return null;
}

/**
 * Validate phone number with fallback to add leading 0
 * If normal validation fails, tries again with '0' prepended
 * @param {string} number - Phone number to validate
 * @param {string} country - Country code (au, nz, my)
 * @returns {object} Validation result with isValid and errorMessage
 */
export function validatePhoneNumberWithLeadingZeroFallback(number, country) {
    // First try normal validation
    const result = validatePhoneNumber(number, country);

    if (result.isValid) {
        return result;
    }

    // If validation failed and number doesn't start with 0, try with leading 0
    const normalized = normalizePhoneNumber(number);
    if (normalized && !normalized.startsWith('0')) {
        const numberWithLeadingZero = `0${normalized}`;
        const resultWithLeadingZero = validatePhoneNumber(
            numberWithLeadingZero,
            country
        );

        if (resultWithLeadingZero.isValid) {
            return {
                isValid: true,
                errorMessage: null,
                usedLeadingZero: true,
                formattedNumber: numberWithLeadingZero,
            };
        }

        // If the original error was "must start with X or 0",
        // return the more specific error from the national format validation
        if (result.errorMessage.includes('Phone number must start with')) {
            return resultWithLeadingZero;
        }
    }

    // Return original error
    return result;
}
